import { observer } from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import Card, { ICardTooltipProps } from "../../../../../components/layouts/card/card";
import useTranslation from "../../../../../translation/hooks/translation-hook";
import GridLineLayout from "../../../../../components/layouts/grid-line-layout/grid-line-layout";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import GridLine from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import Icon from "../../../../../components/icons/icon/icon";
import IconEmail from "../../../../../components/icons/icon-email/icon-email";
import TranslationScope from "../../../../../translation/translation-scope";
import Translation from "../../../../../translation/translation";
import GridLineButtons
	from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import { SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { ButtonIcon } from "../../../../../components/inputs/button-icon/button-icon";
import emailContentListStore from "../../../stores/email-content-list-store";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import { useState } from "react";
import IconTest2 from "../../../../../components/icons/icon-test2/icon-test2";
import NotificationEventStore from "../../../stores/notification-event-store";
import { NotificationTestModal } from "../../notification-test-modal/notification-test-modal";
import { NotificationModal } from "../../../../../components/modal/components/notification-modal/notification-modal";
import IconEdit from "../../../../../components/icons/icon-edit/icon-edit";
import { useNavigate } from "react-router-dom";
import { ShopifyPlatformIndicator } from "./shopify-platform-indicator/shopify-platform-indicator";

import {
	EXCHANGE_ORDER_INVOICE_EMAIL,
	EXCHANGE_ORDER_INVOICE_SCOPE
} from "../draft-order-translations/exchange-order-invoice/constants";
import {
	CHARGED_FEES_INVOICE_EMAIL,
	CHARGED_FEES_INVOICE_SCOPE
} from "../draft-order-translations/charged-fees-invoice/constants";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface EmailTypeCardProps {
    title: string,
    toolbarContent: React.ReactNode,
    onExpandClick: (expanded: boolean) => void,
    isExpanded: boolean;
    events: NotificationEventStore[];
    disabled?: boolean;
    disabledTooltip?: ICardTooltipProps;
    showDraftOrderTranslations: boolean;
}

const EmailTypeCard: React.FC<EmailTypeCardProps> = observer((
    {
        title,
        events,
        toolbarContent,
        isExpanded,
        onExpandClick,
        disabled,
        disabledTooltip,
        showDraftOrderTranslations
    }) => {
    const navigate = useNavigate();
    const { selectedLocale } = useTranslation();

    const emailsContentList = events;
    const channels = emailContentListStore.channels;

    const [testNotification, setTestNotification] = useState<NotificationEventStore>();
    const [hasNotificationIntegration] = useState(emailContentListStore.hasNotificationIntegration);
    const [integrationServiceName] = useState(emailContentListStore.integrationServiceName);
    const [showNotifySuccess, setShowNotifySuccess] = useState(false);
    const [notifyMessage, setNotifyMessage] = useState('');

    const onEditEmailClicked = (emailContentStore: NotificationEventStore) => {
        emailContentListStore.setSelectedEventId(emailContentStore.eventId as string);
        navigate({
            pathname: `${emailContentStore.eventId}`,
        });

    }

    return <Card
        toolbar={{
            title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{title}</FontBody1>,
            content: toolbarContent
        }}
        isExpandable={true}
        isExpanded={isExpanded}
        expandCallback={onExpandClick}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
    >
            <GridLineLayout>
                <div>
                    <FontBody1 color={FONT_COLOR.PRIMARY3} variant={FONT_VARIANT.BOLD}>Emails</FontBody1>
                    <div>
                        <FontBody2 color={FONT_COLOR.PRIMARY3}>
                            {
                                (emailContentListStore.hasNotificationIntegration) ?
                                    'View and test the different email notifications sent for different events during the RMA process. Specify which notifications will be sent and through which platform.' :
                                    'View and test the email notifications sent for different events during the RMA process, and specify which notifications will be sent.'
                            }
                        </FontBody2>
                        {hasNotificationIntegration && <Note type={NOTE_TYPE.INFORMATION} body={
                            `For notifications marked as "Sent by ${integrationServiceName}", create ${integrationServiceName} flows to send them and trigger these flows by the appropriate events.`
                        } />
                        }
                    </div>
                </div>
                {
                    emailsContentList.map((emailContentStore, i) => emailContentStore.show && (
                        <GridLine key={emailContentStore.eventId}
                            onClick={() => emailContentStore.isEditEmailAllowed && onEditEmailClicked(emailContentStore)}
                            settings={{ columns: [{ width: 30, type: 'px' }, { width: 1 }, { width: 0.5 }] }}
                            columns={[
                                <GridLineItem>
                                    <div>
                                        <Icon Component={() => <IconEmail color={"#2F3467"} />} />
                                    </div>
                                </GridLineItem>,

                                <GridLineItem>
                                    <span>
                                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{emailContentStore.title}</FontBody2>
                                        <FontBody2 color={FONT_COLOR.PRIMARY2}>&nbsp;-&nbsp;</FontBody2>
                                        <TranslationScope scope={emailContentStore.scope} locale={selectedLocale}>
                                            <Translation Font={FontBody2} color={FONT_COLOR.PRIMARY2} translationKey={emailContentStore.content} />
                                        </TranslationScope>
                                    </span>

                                </GridLineItem>,
                                <GridLineButtons>
                                    <div onClick={(e) => e.stopPropagation()} style={{ width: '150px' }}>
                                        <SelectDropdown
                                            value={emailContentStore.setting}
                                            options={channels}
                                            onChange={(o) => emailContentStore?.setSetting(o)} />
                                    </div>
                                    <ButtonIcon Component={() => <IconTest2/>} onClick={() => setTestNotification(emailContentStore)} />
                                    {
                                        emailContentStore.isEditEmailAllowed &&
                                        <ButtonIcon Component={() => <IconEdit/>} onClick={() => onEditEmailClicked(emailContentStore)} />
                                    }

                                </GridLineButtons>,
                            ]}
                        />
                    ))
                }

				<ResolveCapability capability={PROFILE_CAPABILITIES.EMAILS}>
					{({ isChargedFeesDraftOrderEmailSupported, isExchangeFeesDraftOrderEmailSupported }) => showDraftOrderTranslations && (
						<>
							{isExchangeFeesDraftOrderEmailSupported && (
								<GridLine
									onClick={() => navigate('exchange-order-invoice-translations')}
									settings={{ columns: [{ width: 30, type: 'px' }, { width: 1 }, { width: 0.5 }] }}
									columns={[
										<GridLineItem>
											<Icon Component={() => <IconEmail color={"#2F3467"} />} />
										</GridLineItem>,

										<GridLineItem>
				                            <span>
				                                <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Exchange Fees Draft Orders</FontBody2>
				                                <FontBody2 color={FONT_COLOR.PRIMARY2}>&nbsp;-&nbsp;</FontBody2>
				                                <TranslationScope scope={EXCHANGE_ORDER_INVOICE_SCOPE} locale={selectedLocale}>
				                                    <Translation Font={FontBody2} color={FONT_COLOR.PRIMARY2} translationKey={EXCHANGE_ORDER_INVOICE_EMAIL.SUBJECT} />
				                                </TranslationScope>
				                            </span>
										</GridLineItem>,

										<GridLineButtons>
											<ShopifyPlatformIndicator />
											<ButtonIcon
												Component={() => <IconEdit/>}
												onClick={() => navigate('exchange-order-invoice-translations')}
											/>
										</GridLineButtons>
									]}
								/>
							)}

							{isChargedFeesDraftOrderEmailSupported && (
								<GridLine
									onClick={() => navigate('charged-fees-invoice-email-translations')}
									settings={{ columns: [{ width: 30, type: 'px' }, { width: 1 }, { width: 0.5 }] }}
									columns={[
										<GridLineItem>
											<Icon Component={() => <IconEmail color={"#2F3467"} />} />
										</GridLineItem>,

										<GridLineItem>
				                        <span>
				                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Charged Fees Draft Order</FontBody2>
				                            <FontBody2 color={FONT_COLOR.PRIMARY2}>&nbsp;-&nbsp;</FontBody2>
				                            <TranslationScope scope={CHARGED_FEES_INVOICE_SCOPE} locale={selectedLocale}>
				                                <Translation Font={FontBody2} color={FONT_COLOR.PRIMARY2} translationKey={CHARGED_FEES_INVOICE_EMAIL.SUBJECT} />
				                            </TranslationScope>
				                        </span>
										</GridLineItem>,

										<GridLineButtons>
											<ShopifyPlatformIndicator />
											<ButtonIcon
												Component={() => <IconEdit/>}
												onClick={() => navigate('charged-fees-invoice-email-translations')}
											/>
										</GridLineButtons>
									]}
								/>
							)}
						</>
					)}
				</ResolveCapability>

            </GridLineLayout>
            {testNotification && <NotificationTestModal
                title="Test Events and Notifications"
                selectedServiceCode={testNotification.setting}
                channels={channels}
                eventName={testNotification.title}
                setTestNotificationData={(testNotificationData) => {
                    testNotificationData.eventId = testNotification.eventId as string;
                    testNotificationData.locale = selectedLocale;
                    console.log(testNotificationData);
                    return testNotification.sendTestNotification(testNotificationData);
                }}
                sendButton={{
                    title: 'Send',
                    onClick: (message) => {
                        setTestNotification(undefined);
                        setNotifyMessage(message);
                        setShowNotifySuccess(true);
                    }
                }}
                discardButton={{
                    title: 'Discard',
                    onClick: () => {
                        setTestNotification(undefined);
                        setNotifyMessage('');
                        setShowNotifySuccess(false);
                    }
                }}
            />
            }
            {showNotifySuccess && <NotificationModal
                title="Test Events and Notifications"
                text={notifyMessage}
                okButton={{
                    title: 'Close',
                    onClick: () => setShowNotifySuccess(false)
                }} />}
    </Card>
});

export default EmailTypeCard;
